import type { ButtonHTMLAttributes, ReactNode } from "react";
import { ArrowUpRight, LoaderCircle, Sparkles } from "lucide-react";
import type { StatusTone } from "@/lib/research";

export function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <div className="brand-lockup" aria-label="内容研究台">
      <span className="brand-symbol"><Sparkles size={18} strokeWidth={2.35} /></span>
      {!compact && <span>内容研究台</span>}
    </div>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: "primary" | "secondary" | "quiet" | "danger";
  loading?: boolean;
  icon?: ReactNode;
};

export function Button({
  className = "",
  tone = "primary",
  loading,
  icon,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={`button button-${tone} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <LoaderCircle className="spin" size={16} /> : icon}
      <span>{children}</span>
    </button>
  );
}

export function LinkButton({
  href,
  children,
  className = "",
  tone = "primary",
  external = false,
}: {
  href: string;
  children: ReactNode;
  className?: string;
  tone?: "primary" | "secondary" | "quiet";
  external?: boolean;
}) {
  return (
    <a
      className={`button button-${tone} ${className}`}
      href={href}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
    >
      <span>{children}</span>
      {external && <ArrowUpRight size={15} />}
    </a>
  );
}

export function StatusBadge({ tone = "neutral", children }: { tone?: StatusTone; children: ReactNode }) {
  return <span className={`status-badge status-${tone}`}>{children}</span>;
}

export function PageHeading({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-heading">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-heading-actions">{actions}</div>}
    </header>
  );
}

export function LoadingCard({ label = "正在读取真实任务状态…" }: { label?: string }) {
  return (
    <div className="loading-card">
      <LoaderCircle className="spin" size={18} />
      <span>{label}</span>
    </div>
  );
}

export function ErrorCard({ message }: { message: string }) {
  return (
    <div className="inline-error" role="alert">
      <strong>暂时无法读取</strong>
      <span>{message}</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  copy,
  action,
}: {
  icon: ReactNode;
  title: string;
  copy: string;
  action?: ReactNode;
}) {
  return (
    <section className="empty-state">
      <span className="empty-icon">{icon}</span>
      <h2>{title}</h2>
      <p>{copy}</p>
      {action && <div>{action}</div>}
    </section>
  );
}
